import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import PlacesFiltersBar from '../components/PlacesFiltersBar';
import PlacesListingContent from '../components/PlacesListingContent';
import PlacesHeader from '../components/PlacesHeader';
import PlacesMap from '../components/PlacesMap';
import GalleryPagination from '../../gallery/components/GalleryPagination';
import {PlaceListing} from '../../admin/artisticobjects/types/Place';
import placesService from '../services/placesService';
import {handleError} from '../../common/utils/errorHandling';
import preferencesService from '../../common/services/preferencesService';

const PlacesListing: React.FC = () => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const [places, setPlaces] = useState<PlaceListing[]>([]);
  const [pageIndex, setPageIndex] = useState(0);

  const initialOuFilter = searchParams.getAll('ou[]');
  const savedFilters = preferencesService.getLocationsFilters();

  const [organizationalUnitIds, setOrganizationalUnitIds] = useState<string[]>(
    initialOuFilter.length > 0 ? initialOuFilter : savedFilters.organizationalUnitIds
  );
  const [hierarchyType, setHierarchyType] = useState<string>(
    initialOuFilter.length > 0 ? savedFilters.hierarchyType : savedFilters.hierarchyType
  );

  const [zoomedIn, setZoomedIn] = useState(() => preferencesService.getGalleryDisplay().zoomedIn);
  const [withDescriptions, setWithDescriptions] = useState(() => preferencesService.getGalleryDisplay().withDescriptions);
  const [columnsCount, setColumnsCount] = useState(() => preferencesService.getGalleryDisplay().columnsCount);
  const [showMap, setShowMap] = useState(() => preferencesService.getGalleryDisplay().showMap || false);
  const [itemsPerPage, setItemsPerPage] = useState(() => preferencesService.getGalleryPagination().itemsPerPage);

  useEffect(() => {
    fetchPlaces();
  }, [organizationalUnitIds, hierarchyType]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({ zoomedIn });
  }, [zoomedIn]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({ withDescriptions });
  }, [withDescriptions]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({ columnsCount });
  }, [columnsCount]);

  useEffect(() => {
    preferencesService.updateGalleryDisplay({ showMap });
  }, [showMap]);

  useEffect(() => {
    preferencesService.updateGalleryPagination({ itemsPerPage });
  }, [itemsPerPage]);

  useEffect(() => {
    preferencesService.updateLocationsFilters({ organizationalUnitIds, hierarchyType });
  }, [organizationalUnitIds, hierarchyType]);

  const fetchPlaces = async () => {
    await handleError(
      async () => {
        const data = await placesService.searchPlaces(organizationalUnitIds, hierarchyType);
        setPlaces(data);
        setPageIndex(0);
      },
      t('common.errors.fetchingPlaces')
    );
  };

  const handleFilterChange = (newOrganizationalUnitIds: string[], newHierarchyType: string) => {
    setOrganizationalUnitIds(newOrganizationalUnitIds);
    setHierarchyType(newHierarchyType);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number): void => {
    setItemsPerPage(newItemsPerPage);
    setPageIndex(0);
  };

  const totalCount = places.length;
  const startIndex = pageIndex * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedPlaces = places.slice(startIndex, endIndex);

  const currentRangeStart = totalCount > 0 ? startIndex + 1 : 0;
  const currentRangeEnd = Math.min(endIndex, totalCount);

  const effectiveColumnsCount = showMap ? Math.min(Math.floor(columnsCount / 2), 4) : columnsCount;

  return (
    <>
      <PlacesFiltersBar
        onFilterChange={handleFilterChange}
        initialFilter={initialOuFilter.length > 0 ? initialOuFilter : organizationalUnitIds}
        initialHierarchyType={hierarchyType}
      />
      <PlacesHeader
        title={t('places.header.title')}
        introduction={t('places.header.introduction')}
        range={`${currentRangeStart}-${currentRangeEnd}`}
        totalCount={totalCount}
        zoomedIn={zoomedIn}
        withDescriptions={withDescriptions}
        columnsCount={columnsCount}
        showMap={showMap}
        onToggleZoom={setZoomedIn}
        onToggleDescriptions={setWithDescriptions}
        onChangeColumns={setColumnsCount}
        onToggleMap={setShowMap}
      />
      {showMap ? (
        <section id="places-listing" className="bg-gray-100 py-8">
          <div className="container mx-auto px-4">
            <div className="flex gap-4 h-[calc(100vh-300px)]">
              <div className="flex-1 overflow-y-auto">
                <PlacesListingContent
                  places={displayedPlaces}
                  zoomedIn={zoomedIn}
                  withDescriptions={withDescriptions}
                  columnsCount={effectiveColumnsCount}
                  skipWrapper={true}
                  skipPagination={true}
                />
              </div>
              <div className="flex-1 h-full">
                <PlacesMap places={places} />
              </div>
            </div>
            <div className="mt-4">
              <GalleryPagination
                pageIndex={pageIndex}
                totalCount={totalCount}
                itemsPerPage={itemsPerPage}
                onSetPageIndex={setPageIndex}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </section>
      ) : (
        <PlacesListingContent
          places={displayedPlaces}
          pagination={(
            <GalleryPagination
              pageIndex={pageIndex}
              totalCount={totalCount}
              itemsPerPage={itemsPerPage}
              onSetPageIndex={setPageIndex}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
          zoomedIn={zoomedIn}
          withDescriptions={withDescriptions}
          columnsCount={columnsCount}
        />
      )}
    </>
  );
};

export default PlacesListing;

